import React, { useEffect, useState } from 'react';
import { useScore } from '../hooks/useScore';
import { useContent } from '../contexts/ContentContext';
import ImageDetailPage from './ImageDetailPage';
import ContentPageHeader from './PageHeader';

// Preserve browsing context when resolving older ID-only shared links.
export default function ImageRoute({ search, onResolve }: { search: string; onResolve: () => void }) {
    const { t } = useContent();
    const { fetchImageUrl } = useScore();
    const [failure, setFailure] = useState<{ search: string; key: 'common.invalidImageLink' | 'common.imageUnavailable' | 'common.imageFetchError' } | null>(null);
    const [attempt, setAttempt] = useState(0);
    const params = new URLSearchParams(search);
    const id = params.get('id');
    const year = params.get('year');
    const month = params.get('month');
    const hash = params.get('hash');
    const invalid = !id || !/^\d+$/.test(id) || Number(id) <= 0 ||
        (year !== null && !/^\d{4}$/.test(year)) || (month !== null && !/^(0?[1-9]|1[0-2])$/.test(month)) ||
        (hash !== null && !/^[a-zA-Z0-9_-]+$/.test(hash));
    const complete = !invalid && !!year && !!month && !!hash;

    useEffect(() => {
        if (invalid || complete || !id) return;
        let active = true;
        setFailure(null);
        fetchImageUrl(id).then(result => {
            if (!active) return;
            if (!result.url) {
                setFailure({ search, key: result.error === 'not_found' ? 'common.imageUnavailable' : 'common.imageFetchError' });
                return;
            }
            try {
                const resolved = new URL(result.url, window.location.origin);
                const next = resolved.searchParams;
                if (!['http:', 'https:'].includes(resolved.protocol) || next.get('view') !== 'image' || next.get('id') !== id ||
                    !/^\d{4}$/.test(next.get('year') || '') || !/^(0?[1-9]|1[0-2])$/.test(next.get('month') || '') ||
                    !/^[a-zA-Z0-9_-]+$/.test(next.get('hash') || '') ||
                    (hash && hash !== next.get('hash')) || (year && year !== next.get('year')) ||
                    (month && Number(month) !== Number(next.get('month')))) throw new Error('Invalid image link');
                const merged = new URLSearchParams(search);
                for (const key of ['year', 'month', 'hash']) merged.set(key, next.get(key)!);
                window.history.replaceState(window.history.state, '', `${window.location.pathname}?${merged}${window.location.hash}`);
                onResolve();
            } catch {
                setFailure({ search, key: 'common.invalidImageLink' });
            }
        });
        return () => { active = false; };
    }, [search, id, year, month, hash, invalid, complete, attempt, fetchImageUrl, onResolve]);

    if (complete) return <ImageDetailPage key={`${id}:${hash}:${year}:${month}`} year={year!} month={month!} imageHash={hash!} />;
    const error = invalid ? 'common.invalidImageLink' : failure?.search === search ? failure.key : null;
    const heading = <ContentPageHeader actions={<a className="lab-button" href="/?page=gallery">{t('imageDetail.backToList')}</a>} />;
    if (error) return <>{heading}<div className="lab-route-error" role="alert"><p>{t(error)}</p>
        <button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button>
    </div></>;
    return <>{heading}<p role="status">{t('common.loading')}</p></>;
}
